import update from 'immutability-helper';
import SelfServeBuilderEnum from 'SelfServeBuilder/SelfServeBuilderConstants';
import SectionOrganizerConstants from './SectionOrganizerConstants';

const uuidV4 = require('uuid/v4');

const initialState = {
  // example of sections array item:
  // {
  //    id: id,
  //    name: NAME,
  //    fieldIds: [ fieldId, groupId... ], (Can contain fieldIds (popupIds) as well as groupIds)
  // }
  sections: [],
  showSample: true,
};

const defaultSectionState = {
  fieldIds: [],
  name: 'SECTION 1',
  isDraggable: false,
  isDroppable: true,
  isMultiField: true,
  isNameEditable: true,
};

const defaultSection = {
  ...defaultSectionState,
  id: uuidV4(),
};

const SectionOrganizerReducer = (state, action) => {
  if (typeof state === 'undefined') {
    return initialState;
  }

  switch (action.type) {
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_CUSTOM_TEMPLATE_DATA_GET_SUCCESS: {
      const sections = action.data.custom_template.field_outline.sections || [defaultSection];
      let showSample = false;
      if (sections[0].fieldIds.length === 0) {
        showSample = true;
      }

      return update(state, {
        sections: { $set: sections },
        showSample: { $set: showSample },
      });
    }
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_FIELD_ADD: {
      // verify new field
      const numOfSections = state.sections.length;
      for (let sectionIdx = 0; sectionIdx < numOfSections; sectionIdx += 1) {
        if (state.sections[sectionIdx].fieldIds.includes(action.fieldId)) {
          return state;
        }
      }

      // grab newest section as id
      const sectionIndexToAdd = state.sections.length - 1;
      const targetSection = state.sections[sectionIndexToAdd];
      const isSectionFull = !targetSection.isMultiField && targetSection.fieldIds.length >= 1;

      // Need a new section because the previous one is full
      // e.g., signature sections can have only a single field
      if (isSectionFull) {
        const newSection = {
          ...defaultSectionState,
          fieldIds: [action.fieldId],
          id: uuidV4(),
          name: 'Section',
        };
        return update(state, {
          showSample: { $set: false },
          sections: {
            $push: [newSection],
          },
        });
      }
      // add new field to section
      return update(state, {
        showSample: { $set: false },
        sections: {
          [sectionIndexToAdd]: {
            fieldIds: {
              $push: [action.fieldId],
            },
          },
        },
      });
    }
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_FIELD_REMOVE: {
      let updatedState = state;
      const numOfSections = state.sections.length;
      let sectionIndex;
      for (let i = 0; i < numOfSections; i += 1) {
        if (state.sections[i].fieldIds.indexOf(action.fieldId) >= 0) {
          sectionIndex = i;
          const fieldIdIdx = state.sections[i].fieldIds.indexOf(action.fieldId);
          const spliceArgs = [fieldIdIdx, 1];

          updatedState = update(state, {
            sections: {
              [i]: {
                fieldIds: {
                  $splice: [spliceArgs],
                },
              },
            },
          });
          break;
        }
      }

      const sectionRemovedFrom = updatedState.sections[sectionIndex];
      const isSectionRemovedFromEmpty = sectionRemovedFrom && sectionRemovedFrom.fieldIds.length === 0;
      if (isSectionRemovedFromEmpty && action.removeEmptySection) {
        // Restore default section if removing only section
        if (state.sections.length <= 1) {
          return update(updatedState, {
            sections: {
              0: {
                name: {
                  $set: 'SECTION 1',
                },
                isMultiField: {
                  $set: true,
                },
                isNameEditable: {
                  $set: true,
                },
              },
            },
          });
        }

        // Remove section
        return update(updatedState, {
          sections: {
            $splice: [[sectionIndex, 1]],
          },
        });
      }

      return updatedState;
    }
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_SECTION_ADD: {
      const newSection = {
        ...defaultSectionState,
        id: uuidV4(),
        isDraggable: action.isDraggable,
        isDroppable: action.isDroppable,
        isMultiField: action.isMultiField,
        name: action.sectionName,
        isNameEditable: action.isNameEditable,
      };

      // Special case when adding a section when only the sample is showing
      if (state.showSample) {
        const newSectionSpliceArgs = [0, state.sections.length, newSection];
        return update(state, {
          showSample: { $set: false },
          sections: {
            $splice: [newSectionSpliceArgs],
          },
        });
      }

      return update(state, {
        showSample: { $set: false },
        sections: {
          $push: [newSection],
        },
      });
    }
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_SECTION_REMOVE: {
      let sectionIndexToAddFields;
      const oldSection = state.sections[action.sectionIndex];
      const numOfFieldIdsToBeMoved = oldSection.fieldIds.length;

      // Try to merge the deleted section's fields into an adjacent section
      const nextSection = state.sections[action.sectionIndex + 1];
      const prevSection = state.sections[action.sectionIndex - 1];
      if (prevSection && prevSection.isMultiField) {
        sectionIndexToAddFields = action.sectionIndex - 1;
      } else if (nextSection && nextSection.isMultiField) {
        sectionIndexToAddFields = action.sectionIndex + 1;
      } else if (numOfFieldIdsToBeMoved > 0) {
        // No adjacent sections are multifield
        // simply rename this section to reflect a change
        return update(state, {
          sections: {
            [action.sectionIndex]: {
              $set: {
                ...defaultSectionState,
                fieldIds: [...oldSection.fieldIds],
                id: oldSection.id,
                name: action.sectionIndex === 0 ? 'SECTION 1' : 'Section',
              },
            },
          },
        });
      }

      let updatedState = state;
      if (sectionIndexToAddFields !== undefined) {
        // remove appropriate fields from old section
        const fieldsIdToMove = oldSection.fieldIds.splice(
          0,
          numOfFieldIdsToBeMoved,
        );
        const startIndex = action.sectionIndex > 0 ?
        state.sections[sectionIndexToAddFields].fieldIds.length :
        0;

        // add appropriate fields to new section
        updatedState = update(state, {
          sections: {
            [sectionIndexToAddFields]: {
              fieldIds: {
                $splice: [[startIndex, 0, ...fieldsIdToMove]],
              },
            },
          },
        });
      }

      // remove target section
      return update(updatedState, {
        sections: {
          $splice: [[action.sectionIndex, 1]],
        },
      });
    }
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_SECTION_MOVE: {
      const section = state.sections[action.sourceIndex];

      return update(state, {
        sections: {
          $splice: [
            [action.sourceIndex, 1],
            [action.destinationIndex, 0, section],
          ],
        },
      });
    }
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_FIELD_MOVE: {
      const sourceSectionIndex = state.sections.findIndex(element => (
        element.id === action.sourceSectionId
      ));
      const destinationSectionIndex = state.sections.findIndex(element => (
        element.id === action.destinationSectionId
      ));

      if (sourceSectionIndex < 0 || destinationSectionIndex < 0) {
        return state;
      }

      const fieldIdToMove = state.sections[sourceSectionIndex].fieldIds[action.sourceIndex];

      // Move fields in two steps so this works
      // if source and destination section are the same
      const updatedState = update(state, {
        sections: {
          [sourceSectionIndex]: {
            fieldIds: {
              $splice: [[action.sourceIndex, 1]],
            },
          },
        },
      });

      return update(updatedState, {
        sections: {
          [destinationSectionIndex]: {
            fieldIds: {
              $splice: [[action.destinationIndex, 0, fieldIdToMove]],
            },
          },
        },
      });
    }
    // Moves all fields below the sourceFieldIndex into the next section
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_SECTION_SPLIT: {
      const sectionIndex = action.sectionIndex;
      const fieldIndex = action.fieldIndex;
      const sourceFields = state.sections[sectionIndex].fieldIds;

      const removeFieldsSpliceArgs = [fieldIndex, sourceFields.length - fieldIndex];
      const sectionToMoveFrom = state.sections[sectionIndex];
      const fieldIdsToMove = sectionToMoveFrom.fieldIds.splice(...removeFieldsSpliceArgs);

      // remove appropriate fields from old section
      const updatedState = update(state, {
        sections: {
          [sectionIndex]: {
            fieldIds: {
              $splice: [removeFieldsSpliceArgs],
            },
          },
        },
      });

      const newSection = {
        ...defaultSectionState,
        fieldIds: [...fieldIdsToMove],
        id: uuidV4(),
        name: action.newSectionName,
      };

      const newSectionSpliceArgs = [
        [action.sectionIndex + 1, 0, newSection],
      ];
      // add appropriate fields to new section
      return update(updatedState, {
        sections: {
          $splice: newSectionSpliceArgs,
        },
      });
    }
    case SectionOrganizerConstants.SELF_SERVE_BUILDER_SECTION_ORGANIZER_SECTION_RENAME: {
      return update(state, {
        sections: {
          [action.sectionIndex]: {
            name: {
              $set: action.sectionName,
            },
          },
        },
      });
    }

    default:
      return state;
  }
};

export default SectionOrganizerReducer;



// WEBPACK FOOTER //
// ./src-react/SelfServePostBuilderSidePanel/SectionOrganizer/SectionOrganizerReducer.jsx